import { api } from '@/api/client';
import type { M2BasisSource } from '@/utils/m2Basis';

// Types matching the backend API contract

export interface Vordering {
  id: number;
  name: string;
  status: string;
  total_m2: number;
  percentage: number;
  collector_amount: number;
  total_amount: number;
  collector_count: number;
  collector_ids: number[];
  created_by: string;
  create_date: string;
  notes: string;
  /** Human-readable "Bericht in Odoo" — read back from the linked Facturatie
   *  project.task description, already HTML-stripped server-side. Empty/absent
   *  when no matching task exists. */
  message?: string;
  foto_rapport_url: string | null;
}

/** A single posted customer invoice on the pinned SO. out_refund entries carry a NEGATIVE amount. */
export interface VorderingInvoice {
  name: string;
  amount: number;
  date: string;
}

export interface AvailableCollectorKring {
  kringnummer: number;
  legpatroon: number;
  length: number;
}

export interface AvailableCollector {
  id: number;
  name: string;
  verdieping: string;
  collector_m2: number;
  fotos_uitvoering: string;
  collector_op_druk: boolean;
  blok: string;
  fase_id: number | null;
  fase_name: string | null;
  materiaallijst_id: number | null;
  materiaallijst_name: string | null;
  systeem: string;
  installateur: string;
  legpatroon: string;
  kringen: AvailableCollectorKring[];
}

export interface ClientContact {
  id: number;
  name: string;
  email: string;
}

/**
 * A collector enriched with its authoritative (picking-based) fase + billing
 * state. Drives the cross-fase Vordering overzicht — `available_collectors`
 * stays as-is for the legacy wizard. `fase_id`/`fase_name` are null when the
 * collector couldn't be resolved to a fase (frontend shows a "Geen fase" bucket).
 */
export interface AllCollector {
  id: number;
  name: string;
  verdieping: string;
  blok: string;
  collector_m2: number;
  collector_op_druk: boolean;
  fotos_uitvoering: string;
  fase_id: number | null;
  fase_name: string | null;
  vordered: boolean;
  vordering_id: number | null;
  vordering_name: string | null;
}

/**
 * One sale order in the project-wide contract breakdown (multi-SO projects).
 * `role` drives the colour-coded tag; `amount_untaxed` is negative for minprijs.
 */
export interface SaleOrderBreakdown {
  id: number;
  /** S-number. */
  name: string;
  client_order_ref: string | null;
  /** "YYYY-MM-DD HH:mm:ss" — format to dd-mm-yyyy for display. */
  date_order: string;
  /** excl. btw — negative for a minprijs SO. */
  amount_untaxed: number;
  offerte_m2: number;
  role: 'basis' | 'meerprijs' | 'minprijs';
  /** This SO's invoiced total (project-wide invoiced is `gefactureerd_total`). */
  invoiced: number;
  /** The invoices behind `invoiced`, each carrying only THIS order's share.
   *  A consolidated invoice (one account.move billing two orders' lines) appears
   *  under BOTH orders, split by what it actually bills on each — so these always
   *  sum to `invoiced`. Credit notes are negative. */
  invoices: VorderingInvoice[];
}

/**
 * A billable sale-order line the user can pick to bill this vordering against.
 * `amount` is the line's contract value (excl. btw); `billed` is how much has
 * already been billed on it across prior vorderingen (so `amount - billed` is the
 * remaining headroom). Section headers and €0 lines are never in this list.
 */
export interface BillableLine {
  id: number;
  name: string;
  /** Extra detail from the Odoo line name (e.g. "■ systeem 1 ■") — distinguishes
   *  two lines that share the same product label. May be empty. */
  detail: string;
  /** Section header the line sits under (e.g. "Installatie"). May be empty. */
  section: string;
  amount: number;
  /** Already billed on this line: the greater of the portal's own vordering ledger
   *  and what Odoo has committed (invoiced + delivered-but-to-invoice). A line
   *  invoiced by hand in Odoo therefore arrives with billed === amount and shows
   *  no headroom. Capped at `amount` by the server. */
  billed: number;
  /** Billed on this line through the PORTAL's own vordering ledger.
   *  NEVER add this to `gefactureerd` — the two OVERLAP (a portal vordering reaches
   *  a milestone → sets qty_delivered → Odoo reports it back as to-invoice, so the
   *  same money appears in both). `billed` is their MAX and the only headroom basis. */
  gevorderd: number;
  /** Billed on this line according to ODOO (invoiced + delivered-but-to-invoice).
   *  Overlaps `gevorderd` — see the warning there. */
  gefactureerd: number;
  /** The sale order this line belongs to — the picker spans ALL the project's SOs
   *  (base contract + meerprijs), so lines are grouped by these. */
  so_id: number;
  so_name: string;
}

/**
 * One requested billing line on a create call: the SO line, its own optional
 * percentage (0..100; omit → server uses the m²-derived suggestion) and an
 * optional description (shown on the created Facturatie task). Presence in the
 * `line_billing` array means the line is selected; a non-empty array makes the
 * vordering bill the contract even with no collectors.
 */
export interface LineBillingEntry {
  line_id: number;
  percentage?: number;
  description?: string;
}

export interface VorderingenResponse {
  vorderingen: Vordering[];
  available_collectors: AvailableCollector[];
  /** Cross-fase overview: every collector with its fase + billing state (additive). */
  all_collectors: AllCollector[];
  /** Interventie photo keys already folded into a vordering → which vordering each is
   *  in, so the UI marks those date-groups «gevorderd» and never re-bills them. */
  interventie_vordered?: Array<{ key: string; vordering_name: string }>;
  fases: Array<{ id: number; name: string }>;
  client_contacts: ClientContact[];
  default_signer_id: number | null;
  /** The m² DENOMINATOR every percentage on this screen is derived from: the
   *  stored legplan, else the offerte m², else 0 (no automatic percentage).
   *  Server-resolved — do not re-derive it here (see utils/m2Basis). */
  legplan_m2: number;
  /** Where legplan_m2 came from. */
  m2_basis: M2BasisSource;
  /** The RAW stored legplan field — 0 when the PL never filled it in. Only for
   *  the warning; never divide by this. */
  legplan_m2_stored: number;
  offerte_m2: number;
  so_line_amount: number;
  cumulative_percentage: number;
  /** Gevorderd — Σ x_total_amount of the project's vorderingen (what the PL put forward). */
  cumulative_amount: number;
  /** Gevorderd scoped to the pinned contract SO — use THIS for the Gevorderd tile,
   *  progress bar and Openstaand (keeps all four tiles consistent on one SO). */
  contract_gevorderd: number;
  /** Gefactureerd — Σ posted out_invoice amount_untaxed − out_refund, scoped to the pinned SO. */
  invoiced_amount: number;
  /** Posted customer invoices on the pinned SO (out_refund entries negative). */
  invoices: VorderingInvoice[];
  /** Client meerprijs — the project's OTHER customer SOs, invoiced separately in Odoo
   *  (NOT via vordering). Read-only display; excluded from the contract-SO tile math.
   *  `invoices` out_refund entries carry a NEGATIVE amount. */
  meerprijs: { invoiced: number; invoices: VorderingInvoice[]; so_count: number };
  /** Per-SO contract breakdown across all the project's customer SOs. The
   *  project-totaal strip + per-SO list render from this (multi-SO projects only).
   *  A minprijs SO carries a negative `amount_untaxed`. */
  saleOrdersBreakdown: SaleOrderBreakdown[];
  /** Σ all SOs' net contract amount (a minprijs nets negative). */
  contract_total: number;
  /** Project-wide invoiced total (across every SO). */
  gefactureerd_total: number;
  /** Base SO m² — for the header. NOT a sum (minprijs/aanpassing SOs repeat m²). */
  offerte_m2_base: number;
  /** True once the WHOLE contract (Σ billable lines) is billed — the create lock will 409. */
  fully_invoiced?: boolean;
  hasExistingInvoices?: boolean;
  next_sequence: number;
  /** Billable SO lines for the per-line picker (each with already-billed headroom). */
  billable_lines: BillableLine[];
}

export async function fetchVorderingen(projectId: number): Promise<VorderingenResponse> {
  return api<VorderingenResponse>(`/api/projects/${projectId}/vorderingen`);
}

export async function createVordering(
  projectId: number,
  data: { collector_ids: number[]; notes?: string; omschrijving?: string; signer_partner_id?: number; signer_name?: string; custom_name?: string; einde_werken?: boolean; legplan_m2_override?: number; interventie_photo_keys?: string[]; foto_rapport_key?: string; include_foto_link?: boolean; line_billing?: LineBillingEntry[] },
): Promise<{ vordering: Vordering; allCollectorsUsed: boolean }> {
  const result = await api<Vordering & { allCollectorsUsed?: boolean }>(`/api/projects/${projectId}/vorderingen`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const { allCollectorsUsed, ...vordering } = result;
  return { vordering, allCollectorsUsed: allCollectorsUsed ?? false };
}

export interface PreviewFotoRapportResult {
  foto_rapport_url: string;
  share_url: string;
  /** Same photos as loose files; null when the ZIP could not be made. */
  zip_share_url: string | null;
  /** In-portal download of that ZIP; null alongside zip_share_url. */
  zip_url: string | null;
  foto_rapport_key: string;
}

export async function previewFotoRapport(
  projectId: number,
  collectorIds: number[],
  interventiePhotoKeys: string[] = [],
): Promise<PreviewFotoRapportResult> {
  return api<PreviewFotoRapportResult>(
    `/api/projects/${projectId}/vorderingen/preview-foto-rapport`,
    {
      method: 'POST',
      body: JSON.stringify({ collector_ids: collectorIds, interventie_photo_keys: interventiePhotoKeys }),
    },
  );
}

export async function updateVordering(
  id: number,
  data: { collector_ids?: number[]; notes?: string },
): Promise<void> {
  await api(`/api/vorderingen/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteVordering(id: number): Promise<void> {
  await api(`/api/vorderingen/${id}`, { method: 'DELETE' });
}

export interface VorderingShareLinks {
  url: string;
  /** Loose-photo ZIP; null for vorderingen made before ZIPs existed. */
  zipUrl: string | null;
}

export async function getShareLink(vorderingId: number): Promise<VorderingShareLinks> {
  const result = await api<VorderingShareLinks>(`/api/vorderingen/${vorderingId}/share-link`);
  return { url: result.url, zipUrl: result.zipUrl ?? null };
}

export async function markFasesGevorderd(
  projectId: number,
  faseIds: number[],
  action: 'gevorderd' | 'einde_werken' = 'gevorderd',
): Promise<void> {
  await api(`/api/projects/${projectId}/fases/mark-gevorderd`, {
    method: 'POST',
    body: JSON.stringify({ fase_ids: faseIds, action }),
  });
}

/**
 * A collector systeem code as Odoo defines it. Not a union: the roster lives in the
 * Odoo selection field and reaches the frontend through /api/systems (stores/systems),
 * so a system added in Odoo is valid here without a deploy.
 */
export type CollectorSysteem = string;

export interface ApproveKringUpdate {
  id: number;
  x_system?: string;
  x_legpatroon?: number;
}

export interface ApproveCollectorPayload {
  systeem?: CollectorSysteem;
  installer_ids?: number[];
  legpatroon?: number;
  kringen?: ApproveKringUpdate[];
}

/**
 * Planning correction (Fase 9a-3): re-point a not-yet-executed collector to
 * another fase. Throws on 409 (op_druk / already vordered) — caller surfaces it.
 */
export async function moveCollectorFase(
  projectId: number,
  collectorId: number,
  toFaseId: number,
): Promise<void> {
  await api(`/api/projects/${projectId}/collectoren/${collectorId}/move-fase`, {
    method: 'POST',
    body: JSON.stringify({ toFaseId }),
  });
}

export async function approveCollectorForVordering(
  projectId: number,
  collectorId: number,
  payload: ApproveCollectorPayload,
): Promise<void> {
  await api(
    `/api/projects/${projectId}/collectoren/${collectorId}/approve-for-vordering`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

// ── Photo mail to the customer (work item vordering-photo-mail) ──────────────

export interface PhotoMailSuggestion {
  partnerId: number | null;
  name: string;
  email: string;
  source: 'ondertekenaar' | 'klantnotitie';
}

export interface PhotoMailSend {
  /** null: a mail found only in Odoo (sent by hand earlier). */
  sendId: string | null;
  state: 'sent' | 'pending' | 'unknown';
  date: string;
  recipients: { partnerId: number | null; name: string; email: string }[];
  by: string | null;
  taskNote: 'written' | 'failed' | 'no-task' | null;
}

export interface PhotoMailDraft {
  vordering_id: number;
  order: { id: number; name: string };
  subject: string;
  default_text: string;
  /** signature_text: the sender's Odoo signature as plain text, exactly what goes under the mail. */
  sender: { name: string; email: string; signature_text: string } | null;
  sender_error: string | null;
  /** made_on_send: no live PDF link yet, a current one is made when Send is pressed.
   *  zip_made_on_send: the loose-photos ZIP exists in storage, its link is made on Send. */
  links: { pdf: string | null; zip: string | null; made_on_send: boolean; zip_made_on_send: boolean } | null;
  links_error: string | null;
  task: { id: number; name: string } | null;
  task_status: 'matched' | 'none' | 'ambiguous';
  /** false: earlier hand-sent mails cannot be checked (no Facturatie task matched). */
  legacy_checkable: boolean;
  signer: PhotoMailSuggestion | null;
  signer_note: string | null;
  suggestions: PhotoMailSuggestion[];
  rule_misses: string[];
  contacts: ClientContact[];
  sends: PhotoMailSend[];
}

export type PhotoMailSendResult =
  // follow: optional, an API from before the sender followed the order answers without it.
  | { status: 'sent'; sendId: string; messageId: number | null; taskNote: 'written' | 'failed' | 'no-task'; follow?: 'ok' | 'failed' }
  | { status: 'unknown'; sendId: string };

export async function getPhotoMailDraft(vorderingId: number): Promise<PhotoMailDraft> {
  return api<PhotoMailDraft>(`/api/vorderingen/${vorderingId}/photo-mail`);
}

export async function sendPhotoMail(
  vorderingId: number,
  payload: { recipients: string[]; text: string; legacy_checked?: boolean },
): Promise<PhotoMailSendResult> {
  return api<PhotoMailSendResult>(`/api/vorderingen/${vorderingId}/photo-mail`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function checkPhotoMail(vorderingId: number, sendId: string): Promise<{ sendId: string; state: string; taskNote: PhotoMailSend['taskNote'] }> {
  return api(`/api/vorderingen/${vorderingId}/photo-mail/${sendId}/check`, { method: 'POST', body: '{}' });
}

export async function markPhotoMailNotSent(vorderingId: number, sendId: string): Promise<{ result: string; taskNote?: PhotoMailSend['taskNote'] }> {
  return api(`/api/vorderingen/${vorderingId}/photo-mail/${sendId}/not-sent`, { method: 'POST', body: '{}' });
}

export async function repairPhotoMailNotes(vorderingId: number): Promise<{ written: number; task: boolean }> {
  return api(`/api/vorderingen/${vorderingId}/photo-mail/repair-notes`, { method: 'POST', body: '{}' });
}
